import React, { useEffect, useRef, useState } from 'react';
import { IdleTimerProvider, useIdleTimerContext } from 'react-idle-timer';
import { useRouter } from 'next/compat/router';

import useUI from 'src/hooks/use-ui';
import { useIdleWarningModal } from 'src/components/modals/idle-warning';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { ConciergeHostPresenceContext } from 'src/concierge/idle/concierge-host-presence';
import { ConciergeIdleWarning } from 'src/concierge/idle/concierge-idle-warning';
import SHARED_ROUTES from 'shared/routes';

// Restarts the shared timer when `signal` changes. A force-closed concierge warning leaves
// the timer idle, and without this the next idle period would never fire.
function IdleTimerRearm({ signal }: { signal: number }): null {
  const idleTimer = useIdleTimerContext();
  const lastSignal = useRef(signal);
  useEffect(() => {
    if (lastSignal.current !== signal) {
      lastSignal.current = signal;
      idleTimer.reset();
    }
  }, [signal, idleTimer]);
  return null;
}

export function IdleDetection({ children }: { children: JSX.Element }): JSX.Element {
  const { isKiosk, isKioskProLoginModalOpen, isConciergeActive } = useUI();
  const { open: openIdleWarningdModal } = useIdleWarningModal();
  const { pathname } = useRouter() ?? {};
  const { dispensary } = useDispensary();
  const { orderTypesConfigV2 = {} } = dispensary ?? {};
  const { kiosk } = orderTypesConfigV2;
  const isTimeoutEnabled = kiosk?.timeout?.enabled;
  const timeoutDurationInSeconds = kiosk?.timeout?.duration ?? 30;
  const timeoutDurationInMilliseconds = timeoutDurationInSeconds * 1000;
  const [isConciergeWarningOpen, setIsConciergeWarningOpen] = useState(false);
  const [rearmSignal, setRearmSignal] = useState(0);
  // Follows the rendered concierge page, not a live LD read: the page latches its gate, so
  // a late LD value must not swap in the shared warning under an open concierge page.
  const [isConciergeHostMounted, setIsConciergeHostMounted] = useState(false);
  const isConciergeWarningAllowed = isKiosk && isConciergeActive && isConciergeHostMounted;

  // IdleDetection persists across navigation, so a stale open warning must not survive
  // leaving the concierge route or the concierge page unmounting.
  // The timer is idle while the warning is open, so a force-close also re-arms it.
  useEffect(() => {
    if (!isConciergeWarningAllowed && isConciergeWarningOpen) {
      setIsConciergeWarningOpen(false);
      setRearmSignal((signal) => signal + 1);
    }
  }, [isConciergeWarningAllowed, isConciergeWarningOpen]);

  const isExcludedPath =
    pathname === SHARED_ROUTES.KIOSK_LOCK ||
    (pathname?.startsWith(SHARED_ROUTES.KIOSK_LOGIN) ?? false) ||
    isKioskProLoginModalOpen;

  // The concierge route keeps the same single timer but gets its own warning:
  // the shared one pauses the timer, needs an explicit button, and offers a
  // destructive exit.
  const onIdle = (): void => {
    if (isConciergeWarningAllowed) {
      setIsConciergeWarningOpen(true);
      return;
    }
    void openIdleWarningdModal();
  };

  // Don't use idle detection if it's not kiosk,
  // not enabled in admin,
  // or if we are already on the kiosk lockscreen
  if (!isKiosk || !isTimeoutEnabled || isExcludedPath) {
    return (
      <ConciergeHostPresenceContext.Provider value={setIsConciergeHostMounted}>
        {children}
      </ConciergeHostPresenceContext.Provider>
    );
  }

  return (
    <ConciergeHostPresenceContext.Provider value={setIsConciergeHostMounted}>
      <IdleTimerProvider timeout={timeoutDurationInMilliseconds} onIdle={onIdle}>
        {children}
        <IdleTimerRearm signal={rearmSignal} />
        {isConciergeWarningOpen && isConciergeWarningAllowed && (
          <ConciergeIdleWarning onClose={() => setIsConciergeWarningOpen(false)} />
        )}
      </IdleTimerProvider>
    </ConciergeHostPresenceContext.Provider>
  );
}
